import { RefObject, useEffect, useState } from 'react'
import { SCROLL_DELAY } from '../context/tour-guide/components/tooltip/index'

export type RelativePosition =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'left-start'
  | 'left-end'
  | 'right'
  | 'right-start'
  | 'right-end'
  | 'overlapping'
type ElementRefOrId = RefObject<HTMLElement | HTMLDivElement> | string

interface Response {
  position: RelativePosition
  ready: boolean
  targetInViewport: boolean
}

/**
 * useRelativePosition - a custom React hook to compute the relative position of two elements.
 *
 * @param {ElementRefOrId} element1 - The first element. This could either be a React ref object or a string representing the element's id.
 * @param {ElementRefOrId} element2 - The second element. This could either be a React ref object or a string representing the element's id.
 * @returns {RelativePosition} - The relative position of element2 with respect to element1. Possible values are 'top', 'bottom', 'left', 'right', or 'overlapping'.
 *
 * The hook uses the getBoundingClientRect method to obtain the dimensions of the elements and calculates their positions relative to each other.
 * It also accounts for page scrolling and window resizing.
 *
 * Example usage with refs:
 * ```
 * import React, { useRef } from 'react';
 * import useRelativePosition from './useRelativePosition';
 *
 * const MyComponent = () => {
 *   const ref1 = useRef<HTMLElement>(null);
 *   const ref2 = useRef<HTMLElement>(null);
 *
 *   const position = useRelativePosition(ref1, ref2);
 *
 *   return (
 *     <>
 *       <div ref={ref1}>Element 1</div>
 *       <div ref={ref2}>Element 2</div>
 *       <p>Element 2 is {position} of Element 1</p>
 *     </>
 *   );
 * };
 *
 * export default MyComponent;
 * ```
 *
 * Example usage with ids:
 * ```
 * import React from 'react';
 * import useRelativePosition from './useRelativePosition';
 *
 * const MyComponent = () => {
 *   const position = useRelativePosition('element1', 'element2');
 *
 *   return (
 *     <p>Element 2 is {position} of Element 1</p>
 *   );
 * };
 *
 * export default MyComponent;
 * ```
 */
export const useBoundPositioning = (
  element1: ElementRefOrId,
  element2: ElementRefOrId,
): Response => {
  const [position, setPosition] = useState<RelativePosition>('overlapping')
  const [ready, setReady] = useState(false)
  const [targetInViewport, setTargetInViewport] = useState(false)
  const [elem1, setElem1] = useState<HTMLElement | null>(null)
  const [elem2, setElem2] = useState<HTMLElement | null>(null)

  const grabElement = (element: ElementRefOrId) =>
    typeof element === 'string'
      ? document.getElementById(element.includes('#') ? element.split('#')[1] : element)
      : element.current

  const isInViewport = (entries: IntersectionObserverEntry[]) => {
    entries.forEach((entry) => {
      setTargetInViewport(entry.isIntersecting)
    })
  }

  /**
   * Calculates the position the arrow should point, relative to the component.
   * @returns
   */
  const calculatePosition = () => {
    if (!targetInViewport) return

    if (elem1 && elem2) {
      //check if inside viewport then start the countdown to show the "triangle"

      const rect1 = elem1.getBoundingClientRect() // component
      const rect2 = elem2.getBoundingClientRect() // chip

      const tooltip = {
        top: rect1.top + window.scrollY,
        bottom: rect1.bottom + window.scrollY,
        left: rect1.left + window.scrollX,
        right: rect1.right + window.scrollX,
      }

      const target = {
        top: rect2.top + window.scrollY,
        bottom: rect2.bottom + window.scrollY,
        left: rect2.left + window.scrollX,
        right: rect2.right + window.scrollX,
      }
      // if the above positions are all zero, then the element is not ready to be positioned
      if (tooltip.top === 0 && tooltip.bottom === 0 && tooltip.left === 0 && tooltip.right === 0) {
        setReady(false)
        return
      } else {
        setReady(true)
      }

      // return the position of the second element relative to the first element (if first element is above second element, return 'bottom')
      const verticalMid = (tooltip.top + tooltip.bottom) / 2
      const horizontalMid = (tooltip.left + tooltip.right) / 2

      if (target.right < tooltip.left) {
        setPosition(
          target.bottom < verticalMid
            ? 'left-start'
            : target.top > verticalMid
              ? 'left-end'
              : 'left',
        )
      } else if (target.left > tooltip.right) {
        setPosition(
          target.bottom < verticalMid
            ? 'right-start'
            : target.top > verticalMid
              ? 'right-end'
              : 'right',
        )
      } else if (target.bottom < tooltip.top) {
        setPosition(
          target.right < horizontalMid
            ? 'top-start'
            : target.left > horizontalMid
              ? 'top-end'
              : 'top',
        )
      } else if (target.top > tooltip.bottom) {
        setPosition(
          target.right < horizontalMid
            ? 'bottom-start'
            : target.left > horizontalMid
              ? 'bottom-end'
              : 'bottom',
        )
      } else {
        // default position
        setPosition('right-start')
      }
    }
  }

  /**
   * IT: Recalculates the position,
   * WHEN: the window is resized or scrolled.
   */
  useEffect(() => {
    const target = grabElement(element2)
    setElem2(target)
    setElem1(grabElement(element1))
    const observer = new IntersectionObserver(isInViewport)
    if (observer && target) {
      observer.observe(target)
    }

    window.addEventListener('resize', calculatePosition)
    window.addEventListener('scroll', calculatePosition)

    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', calculatePosition)
      window.removeEventListener('scroll', calculatePosition)
    }
  }, [element1, element2])

  useEffect(() => {
    // needsd to be placed into a timeout because there's a bounce effect when scrolling that can cause the target and the toolt
    const timeout = setTimeout(() => calculatePosition(), SCROLL_DELAY)
    return () => clearTimeout(timeout)
  }, [targetInViewport])

  return { position, ready, targetInViewport }
}

export default useBoundPositioning
